import {
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import {
  accountInfo,
  accreditationProfile,
  evaluatorRole,
  programProfile,
  Session
} from "../../../../services/session.service";
import { ApiCallService } from "../../../../services/api-call.service";
import { Router, RouterLink, RouterLinkActive } from "@angular/router";
import { ErrorMessageService } from "../../../../services/error-message.service";
import { AuthService } from "../../../../services/auth.service";
import { IdleWatcherService } from "../../../../services/idle-watcher.service";
import { BlockUI, NgBlockUI, BlockUIModule } from "ng-block-ui";
import { Subscription } from "rxjs";
import {first} from "rxjs/operators";
import { RightMenuService } from "../../../../services/right-menu.service";
import {PermissionService} from "../../../../services/permissions.service";
import { UserAvatarComponent } from '../../user-avatar/user-avatar.component';
import { MatIcon } from '@angular/material/icon';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelContent } from '@angular/material/expansion';
import { TeamManagementComponent } from '../team-management/team-management.component';
import { AccountsListComponent } from '../accounts-list/accounts-list.component';

@Component({
    selector: 'app-right-menu',
    templateUrl: './right-menu.component.html',
    styleUrl: './right-menu.component.scss',
    imports: [UserAvatarComponent, BlockUIModule, RouterLink, MatIcon, RouterLinkActive, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelContent, TeamManagementComponent, AccountsListComponent]
})
export class RightMenuComponent implements OnInit, OnDestroy {
  isImpersonatedUser: boolean;
  userIsActingAsEvaluatorForAccreditor: boolean;
  currentEvaluatorRole: evaluatorRole;
  @Input() canShowMenu;
  @Input() currentUser;
  @BlockUI() blockUI: NgBlockUI;
  accreditationProfiles: accreditationProfile[];
  programProfiles: programProfile[];

  currentAccount: accountInfo;
  isAdmin: boolean = false;
  isAccreditor: boolean = false;
  isFederation: boolean = false;
  userOrgName:string = "";

  subscribers: Subscription[] = [];

  lightOptions = [
    {
      label: "Dark mode",
      value: "darkMode"
    },
    {
      label: "Light mode",
      value: "lightMode"
    }
  ];
  lightMode: string = "darkMode";

  @HostListener('document:click', ['$event'])
  clickout(event) {
    const target = event.target as HTMLElement;

    if (!target) {
      return;
    }

    // Check if the clicked element is still in the document
    // If not, it was removed during event handling - ignore it
    if (!document.contains(target)) {
      return;
    }

    if(
      !this.eRef.nativeElement.contains(event.target)  &&
      !event.target.classList.contains("team-management-button") && //so that the outside button can still toggle the menu
      !event.target.closest('.team-management-button') && //is not a descendant of an element with class "team-management-button"
      !event.target.closest('.mat-mdc-dialog-panel') //is not a descendant of a modal
  ) {
      this.rightMenuService.closeMenu();
    }
  }

  private readonly session:Session = inject(Session);
  private readonly apiCall:ApiCallService = inject(ApiCallService);
  private readonly route: Router = inject(Router);
  private readonly errorMessage: ErrorMessageService = inject(ErrorMessageService);
  private readonly auth: AuthService = inject(AuthService);
  private readonly eRef: ElementRef = inject(ElementRef);
  private readonly idleService: IdleWatcherService = inject(IdleWatcherService);
  private readonly rightMenuService = inject(RightMenuService);
  private readonly permissionsService = inject(PermissionService);

  // Access signals directly in the template
  readonly menuOpen = this.rightMenuService.menuOpen;
  readonly activeComponent = this.rightMenuService.activeComponent;
  canManageAccount = computed(() => this.permissionsService.getPermission('accountPermissions', 'manageAccount'));
  totalMembers: WritableSignal<number> = signal(0);

  ngOnInit() {
    this.totalMembers.set(this.session.getAccountStatus().users.length -1 ); // Exclude current user
    this.currentAccount = this.session.getCurrentAccount();
    this.isAdmin = this.session.isAdmin();
    this.isImpersonatedUser = this.session.isImpersonatedUser();
    this.subscribers.push(
      this.session.lightMode.subscribe(lightMode => {
        this.lightMode = lightMode;
        this.setLightMode(this.lightMode);
      }),
    );
    this.userIsActingAsEvaluatorForAccreditor = this.session.isEvaluatorWorkingForOtherInstitution() || this.session.isEvaluatorWithNoPrograms();
    if(this.session.isEvaluatorWithNoPrograms()) { //evaluator with no programs
      //we assume they are only in one accreditor's list
      const evaluatorRole = this.session.getEvaluatorRoles();
      if(evaluatorRole.length > 0) {
        this.userOrgName = evaluatorRole[0].accreditor.abbreviation;
      }
    } else if(this.session.isEvaluatorWorkingForOtherInstitution()){
      this.currentEvaluatorRole = this.session.getEvaluatorRoles().filter((role) =>{
        return role.program.requestKey === this.session.getUser().evaluatorRoleProgramRequestKey;
      })[0];
      this.userOrgName = this.currentEvaluatorRole.programName + ", " + this.currentEvaluatorRole.institutionName + " " + this.currentEvaluatorRole.accreditor.abbreviation;
    }
    this.accreditationProfiles = this.session.getInstitutionDirectoryProfiles();
    this.programProfiles = this.session.getProgramInstitutionDirectoryProfiles();
    this.isAccreditor = this.session.getUserOrganization().isAccreditor;
    this.isFederation = this.session.getUserOrganization().isFederation;
  }

  readonly expansionPanelOpen = computed(() => {
    return this.activeComponent() === 'team-management';
  });

  readonly accountsPanelOpen = computed(() => {
    return this.activeComponent() === 'account-management';
  });

  logout(){
    if(this.session.isShibbolethAuthentication()){
      this.errorMessage.showErrorModal("Success", [{description: "You can now close your browser."}]);
      return;
    }
    const body = document.getElementsByTagName("BODY")[0];
    body.classList.add('block-ui-active', 'block-ui-visible');
    this.apiCall.getData('/aeAMSlogoutUser/', {})
      .subscribe((response) => {
        if(response.success){
          const logOutUrl = this.session.getLogoutUrl();
          this.session.logoutUser();
          body.classList.remove('block-ui-active', 'block-ui-visible');
          if(logOutUrl){
            location.href = logOutUrl;
          }
          else{
            this.route.navigate(['/login']);
            this.idleService.stop();
          }
        }
      }, (error) => {
        this.session.logoutUser();
        body.classList.remove('block-ui-active', 'block-ui-visible');
        this.route.navigate(['/login']);
        this.idleService.stop();
      });
  };

  stopImpersonate() {
    this.session.stopImpersonateUser();
    this.blockUI.start();
    this.apiCall.getData('/aeAMSrestoreImpersonationUser/', {})
      .subscribe( (response) => {
        if (response.success) {
          this.auth.reloadSession()
            .pipe(first())
            .subscribe(()=>{
            this.route.navigate(['/home']);
          });
        }
        else {
          const currentUser = this.session.getUser();
          let userName: string = String();
          if(currentUser){
            userName = currentUser.username;
          }
          this.errorMessage.showErrorModal("There was a problem with stop impersonating user " + userName, response.dataError);
        }
        this.blockUI.stop();
      });
  }
  toggleRightMenu() {
    if (this.menuOpen()) {
      this.rightMenuService.closeMenu();
    } else {
      this.rightMenuService.openMenu('');
    }
  }

  changeLightMode(event) {
    this.session.setLightMode(event);
    this.setLightMode(event);
  }
  setLightMode(lightMode: string) {
    /*this.renderer.removeClass(document.body, 'lightMode');
    this.renderer.removeClass(document.body, 'darkMode');
    this.renderer.addClass(document.body, lightMode);*/
  }

  ngOnDestroy() {
    for(let subscriber of this.subscribers){
      subscriber.unsubscribe();
    }
  }
}
